import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useState } from "react";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { Box, Button, CircularProgress, FormControl, FormHelperText, FormLabel, TextField } from "@material-ui/core";

import { createDepositPaymentIntent } from "../../../../api/Quote/QuoteApi";
import { CARD_ELEMENT_OPTIONS } from "./paymentConstants";
import { usePayDepositStyles } from "./payDeposit.styles";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { EEventType, eventAnalytic } from "../../../../app/analytics";
import { useGetEventSlot, useScheduleQuote } from "../../../../api/EventSlot/EventSlotApi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../app/rootReducer";
import { fetchQuote } from "../../../../store/QuoteSlice";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { quoteDepositAmount } from "../../../../app/utils";

type CardError = {
  type: "validation_error";
  code: string;
  message: string;
};

export const CheckoutForm = ({ quoteId }: { quoteId: string }) => {
  const classes = usePayDepositStyles();
  const { isMoreThanMd } = useBreakpoint();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [cardNumberError, setCardNumberError] = useState<CardError>();
  const [expireDateError, setExpireDateError] = useState<CardError>();
  const [CVVError, setCVVError] = useState<CardError>();
  const [nameOnCard, setNameOnCard] = useState<string>("");
  const [nameOnCardError, setNameOnCardError] = useState<string>();
  const [isSubmitPayment, setIsSubmitPayment] = useState<boolean>(false);
  let history = useHistory();
  const { quote } = useSelector((state: RootState) => state.quote);
  const { data: selectedEventSlot } = useGetEventSlot(quote?.eventSlotId);
  const { mutate: scheduleQuote } = useScheduleQuote();

  const onPay = async () => {
    await onLockAgain();
  };

  const onLockAgain = async () => {
    if (selectedEventSlot && quote) {
      await scheduleQuote({
        quoteId: quote.id,
        eventSlotId: selectedEventSlot.id
      });
      dispatch(fetchQuote(quoteId));
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    if (
      !nameOnCard ||
      !!(elements.getElement(CardNumberElement) as any)._empty ||
      !!(elements.getElement(CardExpiryElement) as any)._empty ||
      !!(elements.getElement(CardCvcElement) as any)._empty
    ) {
      if (!nameOnCard) {
        setNameOnCardError("Name on card is incomplete.");
      }
      if (!!(elements.getElement(CardNumberElement) as any)._empty) {
        setCardNumberError({ message: "Your card number is incomplete.", type: "validation_error", code: "" });
      }
      if (!!(elements.getElement(CardExpiryElement) as any)._empty) {
        setExpireDateError({
          message: "Your card's expiration date is incomplete.",
          type: "validation_error",
          code: ""
        });
      }
      if (!!(elements.getElement(CardCvcElement) as any)._empty) {
        setCVVError({ message: "Your card's security code is incomplete.", type: "validation_error", code: "" });
      }
      return;
    }
    if (cardNumberError || expireDateError || CVVError || nameOnCardError) return;

    try {
      setIsSubmitPayment(true);
      await onPay();

      eventAnalytic.record(EEventType.StripeGateWayInit);
      const clientSecret = await createDepositPaymentIntent(quoteId);

      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
          billing_details: {
            name: nameOnCard
          }
        },
        setup_future_usage: "off_session"
      });

      setIsSubmitPayment(false);
      if (payload.error) {
        toast.error(payload.error.message);
        return;
      }

      history.replace(`/checkout-success/${quoteId}`);
    } catch (error) {
      toast.error(error as string);
      setIsSubmitPayment(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={classes.form}>
        <Box className={classes.formContainer}>
          <FormControl className={classes.formItem}>
            <FormLabel>Card information</FormLabel>
            <Box
              className={`${classes.formItemCard} ${
                cardNumberError || expireDateError || CVVError ? "form-error" : ""
              }`}
            >
              <CardNumberElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(e) => setCardNumberError(e.error)}
                className="card-number"
              />
              <Box display={"flex"} alignItems={"center"} width={"100%"} justifyContent={"space-between"}>
                <CardExpiryElement
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={(e) => setExpireDateError(e.error)}
                  className="card-expiry"
                />
                <CardCvcElement
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={(e) => setCVVError(e.error)}
                  className="card-cvc"
                />
                <Box
                  position={"absolute"}
                  display={"flex"}
                  top={isMoreThanMd ? "27px" : "25px"}
                  right={"18px"}
                  alignItems={"center"}
                >
                  <img src="images/checkout/visa.svg" alt="visa" width={"30px"} />
                  <img src="images/checkout/mastercard.svg" alt="mastercard" width={"30px"} />
                  <img src="images/checkout/american-express.svg" alt="american-express" width={"30px"} />
                </Box>
              </Box>
            </Box>
            {(cardNumberError || expireDateError || CVVError) && (
              <FormHelperText id="card_error" style={{ color: "#eb1c26" }} color="#eb1c26">
                {cardNumberError?.message || expireDateError?.message || CVVError?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl className={`${classes.formItem} ${nameOnCardError ? "form-error" : ""}`}>
            <FormLabel htmlFor="name">Name on card</FormLabel>
            <TextField
              id="name"
              variant="outlined"
              value={nameOnCard}
              onChange={(e) => {
                setNameOnCard(e.target.value);
                setNameOnCardError(undefined);
              }}
              aria-describedby="Name on card"
            />
            {nameOnCardError && (
              <FormHelperText id="my-helper-text" style={{ color: "#eb1c26" }} color="#eb1c26">
                {nameOnCardError}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box className={classes.formContainer}>
          <Button type="submit" className={`${classes.payBtn} full`}>
            Pay €{quoteDepositAmount(quote)} Deposit
          </Button>
        </Box>
        <Box className={classes.formContainer}>
          <Box className={classes.note}>
            Book your installation with a €{quoteDepositAmount(quote)} deposit to secure your price
          </Box>
          <Box className={classes.privacy}>
            By placing your order, you agree to our{" "}
            <a href="https://www.swyftenergy.ie/terms-and-conditions-servicing">terms of service</a> and{" "}
            <a href="https://www.swyftenergy.ie/privacy-policy">privacy policy</a>
          </Box>
        </Box>
      </form>

      {isSubmitPayment && (
        <Box
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height="100%"
          bgcolor={"rgba(255,255,255,0.5)"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
    </>
  );
};
